import { Fragment } from 'react';
import { Link} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Transition } from '@headlessui/react';
import { Menu as MenuIcon, Bell, CircleUser, Settings as SettingsIcon, LogOut, CreditCard } from 'lucide-react';
import { CompactThemeToggle } from '../../components/ui/BootstrapThemeToggle';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { SengiCoinsWidget } from '../../components/ui/SengiCoinsWidget';
import { useAppContext } from '../../contexts/AppContext';
import { useUserHook } from '../../hooks/UserHook';
import { useIsMobile } from '../../hooks/useIsMobile';
import { FlagIcon } from '../../components/ui/FlagIcon';
interface TopbarProps {
    icon: string;
    containerClass?: string;
    logoutHandler: () => void;
    onSidebarToggle: () => void;
    sidebarCollapsed: boolean;
};

export const Topbar =({
    icon,
    containerClass,
    sidebarCollapsed,
    logoutHandler,
    onSidebarToggle
}: TopbarProps) => {
  // Echter eingeloggter User (PortalBackend) statt des toten demo-auth-Stores.
  const [user] = useUserHook();
  const { t, i18n } = useTranslation('common');
  const { effectiveTheme } = useBootstrapTheme();
  const context = useAppContext();
  const isMobile = useIsMobile();
  const currentLang = i18n.resolvedLanguage ?? i18n.language;

  const handleLogout = () => {
    logoutHandler();
  };

  return (
    <>
      { context.showTopbar
        ? <nav 
            className={`${containerClass ?? ''} navbar navbar-expand-lg bg-white border-bottom px-3 d-flex align-items-center justify-content-between fixed-top`} 
            style={{
              height: '64px',
              zIndex: 1000,
              background: effectiveTheme === 'dark'
                ? 'linear-gradient(145deg, rgba(20, 38, 80, 0.95) 0%, rgba(15, 23, 42, 0.98) 50%, rgba(30, 41, 59, 0.95) 100%)'
                : 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 50%, rgba(241, 245, 249, 0.95) 100%)',
              backdropFilter: 'blur(12px)',
              borderBottom: effectiveTheme === 'dark'
                ? '1px solid rgba(96, 165, 250, 0.2)'
                : '1px solid rgba(59, 130, 246, 0.1)',
              boxShadow: effectiveTheme === 'dark'
                ? '0 8px 32px rgba(59, 130, 246, 0.1), 0 2px 8px rgba(96, 165, 250, 0.05)'
                : '0 8px 32px rgba(59, 130, 246, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)'
            }}
          >
            {/* Left side */}
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              {/* Menu toggle between icons-only and icons-with-text */}
              <button
                className={`btn btn-link text-body p-2 ${isMobile ? 'me-1' : 'me-3'}`}
                onClick={onSidebarToggle}
                style={{ width: '40px', height: '40px', flexShrink: 0 }}
                title={sidebarCollapsed ? 'Show menu with text' : 'Show icons only'}
              >
                <MenuIcon size={20} strokeWidth={2} />
              </button>

              {/* Logo and brand */}
              <Link
                to="/dashboard"
                className="navbar-brand d-flex align-items-center text-decoration-none"
                style={{ minWidth: 0, overflow: 'hidden' }}
              >
                <img src={icon} alt='Sengi Logo' style={{height: isMobile ? '34px' : '50px'}}/>
              </Link>
            </div>

            {/* Spacer for centering right side items */}
            <div style={{ flex: 1 }} />

            {/* Right side items - theme toggle + notifications drop on mobile (not enough
                width for all 4 controls, see TASKS.md Task #77 Nachtrag); theme stays
                reachable via /settings, notifications button was never functional anyway. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '12px' }}>
              {/* SengiCoins Widget */}
              <SengiCoinsWidget />
              {/* Theme Toggle */}
              {!isMobile && <CompactThemeToggle />}
              {/* Notifications */}
              {!isMobile && (
                <button
                  className="btn btn-link text-body p-2 rounded-circle"
                  style={{ width: '40px', height: '40px' }}
                >
                  <Bell size={20} strokeWidth={2} />
                </button>
              )}

              {/* Profile dropdown */}
              <Menu as="div" style={{ position: 'relative' }}>
                <div>
                  <Menu.Button
                    className="btn btn-link p-2 rounded-circle"
                    style={{ width: '40px', height: '40px' }}
                  >
                    <CircleUser size={24} strokeWidth={2} color="#9ca3af" />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className="sengi-menu text-body position-absolute end-0 mt-2"
                    style={{
                      width: '220px',
                      outline: 'none'
                    }}
                  >
                    <div className="sengi-menu-header">
                      <div className="sengi-menu-label mb-1">{t('userMenu.signedInAs')}</div>
                      <p className="fw-medium mb-0">{user?.user.displayName || user?.user.userName || '…'}</p>
                      {user?.user.displayName && user.user.displayName !== user.user.userName && (
                        <p className="small text-secondary mb-0">{user.user.userName}</p>
                      )}
                    </div>
                    <div className="sengi-menu-divider" />
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/billing"
                          className={`sengi-menu-item ${active ? 'active' : ''}`}
                        >
                          <CreditCard size={15} strokeWidth={2} />
                          {t('userMenu.billing')}
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/settings"
                          className={`sengi-menu-item ${active ? 'active' : ''}`}
                        >
                          <SettingsIcon size={15} strokeWidth={2} />
                          {t('userMenu.settings')}
                        </Link>
                      )}
                    </Menu.Item>
                    {/* Eigener Sprach-Link mit Flagge (Nutzer-Wunsch): sofort als "Sprache"
                        erkennbar, springt per Deep-Link direkt in den Sprach-Tab der Settings. */}
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/settings?tab=language"
                          className={`sengi-menu-item ${active ? 'active' : ''}`}
                        >
                          <FlagIcon lang={currentLang} height={14} />
                          {t('language.label')}
                        </Link>
                      )}
                    </Menu.Item>
                    <div className="sengi-menu-divider" />
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`sengi-menu-item ${active ? 'active' : ''}`}
                        >
                          <LogOut size={15} strokeWidth={2} />
                          {t('userMenu.logout')}
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </nav>
        : <nav 
          className="navbar navbar-expand-lg bg-white border-bottom px-3 d-flex align-items-center justify-content-between fixed-top" 
          style={{
            height: '64px',
            zIndex: 1000,
            background: effectiveTheme === 'dark'
              ? 'linear-gradient(145deg, rgba(20, 38, 80, 0.95) 0%, rgba(15, 23, 42, 0.98) 50%, rgba(30, 41, 59, 0.95) 100%)'
              : 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 50%, rgba(241, 245, 249, 0.95) 100%)',
            backdropFilter: 'blur(12px)',
            borderBottom: effectiveTheme === 'dark'
              ? '1px solid rgba(96, 165, 250, 0.2)'
              : '1px solid rgba(59, 130, 246, 0.1)',
            boxShadow: effectiveTheme === 'dark'
              ? '0 8px 32px rgba(59, 130, 246, 0.1), 0 2px 8px rgba(96, 165, 250, 0.05)'
              : '0 8px 32px rgba(59, 130, 246, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Left side */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Logo and brand */}
            <div className="navbar-brand d-flex align-items-center text-decoration-none">
              <img src={icon} alt='Sengi Logo' style={{height: '50px'}}/>
            </div>
          </div>
        </nav>
      }
    </>
    
  );
}